import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Filter, Grid, List, Copy, Eye, Edit, Star, 
  MessageSquare, Image, LayoutGrid, Smartphone, Tag,
  ShoppingCart, Car, Shield, CreditCard, Gem, Sparkles, Shirt, Wrench,
  Building, Heart, Package, Bell, Megaphone, Users, Plane
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Industry icons mapping
const industryIcons = {
  retail: ShoppingCart,
  supermarket: ShoppingCart,
  ecommerce: ShoppingCart,
  automotive: Car,
  auto_parts: Wrench,
  insurance: Shield,
  finance: CreditCard,
  jewelry: Gem,
  cosmetics: Sparkles,
  fashion: Shirt,
  healthcare: Heart,
  travel: Plane,
  food: Building,
  general: MessageSquare
};

// Category icons mapping
const categoryIcons = {
  marketing: Megaphone,
  notification: Bell,
  promotion: Tag,
  security: Shield,
  loyalty: Star,
  delivery: Package,
  appointment: Users,
  payment: CreditCard,
  customer_service: MessageSquare,
  feedback: MessageSquare
};

// Industry labels in French
const industryLabels = {
  retail: 'Grande Distribution',
  supermarket: 'Supermarchés',
  ecommerce: 'E-commerce',
  automotive: 'Automobile',
  auto_parts: 'Pièces Auto',
  insurance: 'Assurance',
  finance: 'Finance / Crédit',
  jewelry: 'Bijouterie',
  cosmetics: 'Parfumerie / Beauté',
  fashion: 'Mode',
  healthcare: 'Santé',
  travel: 'Voyage',
  food: 'Restauration',
  general: 'Général'
};

// Category labels in French
const categoryLabels = {
  marketing: 'Marketing',
  notification: 'Notification',
  promotion: 'Promotion',
  security: 'Sécurité',
  loyalty: 'Fidélisation',
  delivery: 'Livraison',
  appointment: 'Rendez-vous',
  payment: 'Paiement',
  customer_service: 'Service Client',
  feedback: 'Feedback'
};

// RCS Type labels and icons
const rcsTypeLabels = {
  rcs_basic: 'RCS Basic',
  rcs_rich: 'RCS Rich',
  rcs_chat: 'RCS Chat'
};

const rcsTypeDescriptions = {
  rcs_basic: 'Texte simple',
  rcs_rich: 'Cartes enrichies',
  rcs_chat: 'Conversationnel'
};

const rcsTypeColors = {
  rcs_basic: 'bg-gray-100 text-gray-700',
  rcs_rich: 'bg-blue-100 text-blue-700',
  rcs_chat: 'bg-purple-100 text-purple-700'
};

export default function TemplateLibrary({ embedded = false }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedRcsType, setSelectedRcsType] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [rcsTypes, setRcsTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      let url = `${API_URL}/api/v2/templates/library?limit=100`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;
      if (selectedIndustry !== 'all') url += `&industry=${selectedIndustry}`;
      if (selectedRcsType !== 'all') url += `&rcs_type=${selectedRcsType}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        let templatesData = data.templates || [];
        
        // Filter by favorites if enabled
        if (showFavoritesOnly) {
          templatesData = templatesData.filter(t => favoriteIds.has(t.id));
        }
        
        setTemplates(templatesData);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, selectedIndustry, selectedRcsType, search, showFavoritesOnly, favoriteIds]);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/templates/library/user/favorite-ids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoriteIds(new Set(data.favorite_ids || []));
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
    }
  }, [token]);

  const fetchFilters = useCallback(async () => {
    try {
      const [catRes, indRes, rcsRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/templates/library/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/v2/templates/library/industries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/v2/templates/library/rcs-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }
      if (indRes.ok) {
        const indData = await indRes.json();
        setIndustries(indData);
      }
      if (rcsRes.ok) {
        const rcsData = await rcsRes.json();
        setRcsTypes(rcsData);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchFavorites();
    fetchFilters();
  }, [fetchFavorites, fetchFilters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDuplicate = async (templateId) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/templates/library/${templateId}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template dupliqué avec succès');
        fetchTemplates();
      } else {
        toast.error('Erreur lors de la duplication');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleToggleFavorite = async (e, templateId) => {
    e.stopPropagation(); // Prevent card click
    
    const isFavorite = favoriteIds.has(templateId);
    
    try {
      const response = await fetch(`${API_URL}/api/v2/templates/library/${templateId}/favorite`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const newFavorites = new Set(favoriteIds);
        if (isFavorite) {
          newFavorites.delete(templateId);
          toast.success('Retiré des favoris');
        } else {
          newFavorites.add(templateId);
          toast.success('Ajouté aux favoris ⭐');
        }
        setFavoriteIds(newFavorites);
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleUseTemplate = async (templateId) => {
    await fetch(`${API_URL}/api/v2/templates/library/${templateId}/use`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Template sélectionné');
    // Navigate to campaign creation with this template
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const getMessageTypeIcon = (type) => {
    const icons = {
      text: MessageSquare,
      media: Image,
      rich_card: LayoutGrid,
      carousel: Grid
    };
    const Icon = icons[type] || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  const renderMobilePreview = (template) => {
    const content = template.content;
    
    return (
      <div className="w-72 h-[500px] bg-gray-100 rounded-3xl p-2 shadow-xl mx-auto">
        <div className="bg-white h-full rounded-2xl overflow-hidden flex flex-col">
          {/* Status bar */}
          <div className="h-6 bg-gray-900 flex items-center justify-center">
            <div className="w-20 h-1 bg-gray-700 rounded-full" />
          </div>
          
          {/* Chat header */}
          <div className="px-4 py-3 border-b flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <Building className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-sm">B2RCS</p>
              <p className="text-xs text-gray-500">Message RCS</p>
            </div>
          </div>
          
          {/* Message content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {content.message_type === 'text' && (
              <div className="bg-gray-100 rounded-lg p-3 max-w-[85%]">
                <p className="text-sm whitespace-pre-wrap">{content.text}</p>
              </div>
            )}
            
            {content.message_type === 'rich_card' && (
              <div className="bg-white border rounded-lg overflow-hidden max-w-[85%] shadow-sm">
                {content.media_url && (
                  <div className="h-32 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                    <Image className="w-12 h-12 text-white/50" />
                  </div>
                )}
                <div className="p-3">
                  {content.title && (
                    <p className="font-semibold text-sm mb-1">{content.title}</p>
                  )}
                  {content.description && (
                    <p className="text-xs text-gray-600 whitespace-pre-wrap">{content.description}</p>
                  )}
                </div>
                {content.suggestions?.length > 0 && (
                  <div className="px-3 pb-3 flex flex-wrap gap-2">
                    {content.suggestions.slice(0, 3).map((s, i) => (
                      <button key={i} className="text-xs px-3 py-1.5 rounded-full border border-indigo-500 text-indigo-600 hover:bg-indigo-50">
                        {s.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {content.message_type === 'carousel' && content.carousel_cards && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                {content.carousel_cards.map((card, i) => (
                  <div key={i} className="flex-shrink-0 w-48 bg-white border rounded-lg overflow-hidden shadow-sm">
                    <div className="h-24 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                      <Image className="w-8 h-8 text-white/50" />
                    </div>
                    <div className="p-2">
                      <p className="font-semibold text-xs mb-1 truncate">{card.title}</p>
                      <p className="text-xs text-gray-600 line-clamp-2">{card.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Input bar */}
          <div className="p-3 border-t">
            <div className="bg-gray-100 rounded-full px-4 py-2 text-xs text-gray-400">
              Écrire un message...
            </div>
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="space-y-6" data-testid="template-library">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('templates.library')}</h1>
          <p className="text-gray-600 mt-1">
            {templates.length} templates disponibles pour vos campagnes RCS
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher un template..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-templates"
                  />
                </div>
              </div>
              
              {/* Favorites Filter Button */}
              <Button
                variant={showFavoritesOnly ? 'default' : 'outline'}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`gap-2 ${showFavoritesOnly ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                data-testid="filter-favorites"
              >
                <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-white' : ''}`} />
                Favoris ({favoriteIds.size})
              </Button>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48" data-testid="filter-category">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {categoryLabels[cat.category] || cat.category} ({cat.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger className="w-48" data-testid="filter-industry">
                  <Building className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Secteur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous secteurs</SelectItem>
                  {industries.map(ind => (
                    <SelectItem key={ind.industry} value={ind.industry}>
                      {industryLabels[ind.industry] || ind.industry} ({ind.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedRcsType} onValueChange={setSelectedRcsType}>
                <SelectTrigger className="w-44" data-testid="filter-rcs-type">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type RCS" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types RCS</SelectItem>
                  {rcsTypes.map(type => (
                    <SelectItem key={type.rcs_type} value={type.rcs_type}>
                      {rcsTypeLabels[type.rcs_type] || type.rcs_type} ({type.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* RCS Type Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-500 py-1.5">Type RCS :</span>
          {Object.entries(rcsTypeLabels).map(([key, label]) => {
            const isActive = selectedRcsType === key;
            const colorClass = rcsTypeColors[key];
            return (
              <Button
                key={key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedRcsType(isActive ? 'all' : key)}
                className={`gap-1.5 ${!isActive ? colorClass : ''}`}
                data-testid={`rcs-type-${key}`}
              >
                <span className="text-xs">{label}</span>
                <span className="text-[10px] opacity-70">({rcsTypeDescriptions[key]})</span>
              </Button>
            );
          })}
        </div>

        {/* Industry Quick Filters */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(industryLabels).slice(0, 8).map(([key, label]) => {
            const Icon = industryIcons[key] || Building;
            const isActive = selectedIndustry === key;
            return (
              <Button
                key={key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedIndustry(isActive ? 'all' : key)}
                className="gap-2"
              >
                <Icon className="w-4 h-4" />
                {label}
              </Button>
            );
          })}
        </div>

        {/* Templates Grid/List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-gray-500">Chargement des templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun template trouvé</p>
              <p className="text-sm text-gray-400 mt-1">Modifiez vos filtres pour voir plus de résultats</p>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => {
              const IndustryIcon = industryIcons[template.industry] || Building;
              const CategoryIcon = categoryIcons[template.category] || Tag;
              
              return (
                <Card key={template.id || template.name} className="hover:shadow-lg transition-shadow" data-testid={`template-card-${template.name}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {getMessageTypeIcon(template.content?.message_type)}
                        <Badge variant="outline" className="text-xs">
                          {template.content?.message_type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Favorite Star Button */}
                        <button
                          onClick={(e) => handleToggleFavorite(e, template.id)}
                          className={`p-1.5 rounded-full transition-all hover:scale-110 ${
                            favoriteIds.has(template.id)
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`}
                          data-testid={`favorite-${template.id}`}
                          title={favoriteIds.has(template.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                          <Star className={`w-5 h-5 ${favoriteIds.has(template.id) ? 'fill-yellow-500' : ''}`} />
                        </button>
                        {template.is_system && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                            Système
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-2">{template.name}</CardTitle>
                    <CardDescription className="line-clamp-2">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* RCS Type Badge */}
                      {template.rcs_type && (
                        <Badge className={`text-xs ${rcsTypeColors[template.rcs_type] || 'bg-gray-100 text-gray-700'}`}>
                          {rcsTypeLabels[template.rcs_type] || template.rcs_type}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="gap-1">
                        <IndustryIcon className="w-3 h-3" />
                        {industryLabels[template.industry] || template.industry}
                      </Badge>
                      <Badge variant="secondary" className="gap-1">
                        <CategoryIcon className="w-3 h-3" />
                        {categoryLabels[template.category] || template.category}
                      </Badge>
                    </div>
                    
                    {template.variables?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-1">Variables :</p>
                        <div className="flex flex-wrap gap-1">
                          {template.variables.slice(0, 4).map(v => (
                            <code key={v} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {`{${v}}`}
                            </code>
                          ))}
                          {template.variables.length > 4 && (
                            <span className="text-xs text-gray-400">+{template.variables.length - 4}</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openPreview(template)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Aperçu
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicate(template.id)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handleUseTemplate(template.id)}
                      >
                        Utiliser
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(template => {
              const IndustryIcon = industryIcons[template.industry] || Building;
              
              return (
                <Card key={template.id || template.name} className="hover:bg-gray-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                        {getMessageTypeIcon(template.content?.message_type)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{template.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{template.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Favorite Star Button */}
                        <button
                          onClick={(e) => handleToggleFavorite(e, template.id)}
                          className={`p-1.5 rounded-full transition-all hover:scale-110 ${
                            favoriteIds.has(template.id)
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`}
                          title={favoriteIds.has(template.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                          <Star className={`w-5 h-5 ${favoriteIds.has(template.id) ? 'fill-yellow-500' : ''}`} />
                        </button>
                        <Badge variant="secondary" className="gap-1">
                          <IndustryIcon className="w-3 h-3" />
                          {industryLabels[template.industry]}
                        </Badge>
                        <Button variant="outline" size="sm" onClick={() => openPreview(template)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => handleUseTemplate(template.id)}>
                          Utiliser
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name}</DialogTitle>
              <DialogDescription>{selectedTemplate?.description}</DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* Template Details */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-500">Type de message</Label>
                  <p className="font-medium capitalize">{selectedTemplate?.content?.message_type}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-500">Secteur</Label>
                  <p className="font-medium">{industryLabels[selectedTemplate?.industry]}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-500">Catégorie</Label>
                  <p className="font-medium">{categoryLabels[selectedTemplate?.category]}</p>
                </div>
                
                {selectedTemplate?.variables?.length > 0 && (
                  <div>
                    <Label className="text-sm text-gray-500">Variables à personnaliser</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedTemplate.variables.map(v => (
                        <code key={v} className="text-sm bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                          {`{${v}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedTemplate?.tags?.length > 0 && (
                  <div>
                    <Label className="text-sm text-gray-500">Tags</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTemplate.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleDuplicate(selectedTemplate?.id)}
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Dupliquer et modifier
                  </Button>
                  <Button
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => {
                      handleUseTemplate(selectedTemplate?.id);
                      setPreviewOpen(false);
                    }}
                  >
                    Utiliser ce template
                  </Button>
                </div>
              </div>
              
              {/* Mobile Preview */}
              <div className="flex justify-center items-start">
                {selectedTemplate && renderMobilePreview(selectedTemplate)}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
}
